/**
 * RecordChannelReservationForm — the WIRED "Record a channel reservation"
 * feature (SCRUM-350).
 *
 * A `modules/booking` feature that manually records a LIVE, non-integrated
 * channel booking (a real OTA booking that did NOT arrive via the channel —
 * failed sync / channel-account change) through the ALREADY-SHIPPED ingest
 * path (`POST /organizations/{slug}/reservations/ingest`; FSM / snapshot
 * UNCHANGED). It is the sanctioned SEPARATE surface to the local "New
 * reservation" form, which correctly rejects `ChannelCollect` types
 * (`channel_collect_not_locally_creatable`, SCRUM-280 option A).
 *
 * Mirrors `CreateReservationForm` conventions: React Hook Form + a Zod
 * resolver over a LOCAL form schema that LAYERS form-only refinements onto
 * `ingestReservationSchema` (@artemis/validators — never redefined; FE rule
 * §4), the property-picker `FormSelect`, and the cross-field
 * `departure > arrival` rule.
 *
 * ## Fields
 *   - **Channel** (`select#rc-channel`) — options are reservation types that are
 *     `collectionModel === 'ChannelCollect'` AND carry a non-null
 *     `channelProcessor` (`useReservationTypes()`). ChannelCollect means the OTA
 *     is the merchant of record, so the booking is net-remittance and is
 *     recorded verbatim (not re-priced) — the exact case this surface exists
 *     for. PropertyCollect channel types (VRBO / Booking / Expedia) are excluded
 *     even though they sync, because the PMC is the merchant of record and they
 *     go through the normal priced flow. Selecting one sets `reservationTypeKey`
 *     (its `internalKey`); the matching `processor` (its `channelProcessor`) is
 *     derived at submit — the two are the SSOT-linked pair the dedup index keys
 *     on.
 *   - **Status** (`select#rc-status`) — LIVE blocking statuses only (BKD / CI /
 *     CO), labelled via the shared `statusLabelFor` (no raw wire code in copy).
 *   - **Property** (`select#rc-property`) — the create form's
 *     `usePropertyNameLookup` id→name picker (a phone-in agent picks by NAME).
 *   - **Arrival / Departure** — `type="date"` with a cross-field
 *     `departure > arrival` refinement (ISO `YYYY-MM-DD` compares lexically).
 *   - **channelReservationId** — optional; blank → wire `null` (keyless →
 *     CREATE-only, never reconciles); a value → future channel reconciliation.
 *   - **netRemittanceCents** — REQUIRED money field. The Zod field
 *     (`netRemittanceDollarsSchema`) validates the string (non-negative,
 *     numeric, ≤2 decimals, required); the integer cents are produced at submit
 *     via `dollarsToCents(Number(...))` from `@artemis/validators` — the SINGLE
 *     conversion path (CLAUDE.md rule 2 / FE rule §4/§6), NO local formula.
 *   - **Guest** — inline `{ displayName, email, phone }` (mirror create form;
 *     no picker — YAGNI).
 *   - **guestCount** — OPTIONAL occupancy (int ≥ 1). Mirrors the create form's
 *     occupancy number-field style. When the operator enters a value it maps
 *     into the ingest payload's `guestCount`; left blank it is OMITTED from the
 *     payload (never coerced to 0/1) so the BE applies its `?? 1` default. Now
 *     that the shipped `IngestInput` carries `guestCount?: number` (SCRUM-350
 *     step 6b) the value FLOWS to the wire — no silent drop (CLAUDE.md SCRUM-19).
 *
 * `heardAbout` + `channelId` are omitted (Phase-1; D5 — no `'OTA'` auto-derive).
 *
 * ## Overlap hard-block
 *   `useReservationAvailability({ propertyId, from: arrival, to: departure })`
 *   is ADR-0026 conflict-group-aware server-side. If any returned
 *   `blockedNight.date` falls in the half-open window `[arrival, departure)`,
 *   submission is BLOCKED and an inline `role="alert"` renders with a link to
 *   the conflicting reservation. An empty result — or a night exactly on the
 *   exclusive departure date — does NOT block. The BE `overbooked` quarantine
 *   remains the race backstop.
 *
 * ## Outcomes (all four render honestly; `outcome` is OPTIONAL on the DTO)
 *   - `created`         → navigate to detail with a `success` flash.
 *   - `updated` / `noop` → navigate to detail with an honest `info` flash.
 *   - `overbooked`      → NOT success; inline `role="alert"` quarantine copy
 *     (no navigate; no raw reservationId / processor slug leaked).
 *   - `undefined`/unknown → generic non-success alert (never mislabelled as
 *     created).
 *
 * A persistent honest banner states the booking will NOT sync (entered
 * manually, not delivered by the channel; net remittance recorded verbatim,
 * not re-priced).
 *
 * **Tokens (ADR frontend-0003, SCRUM-78/79)**, kit-composed (TextField /
 * Button / Card), a11y WCAG 2.1 AA, axe-clean light + dark. No `<main>` — the
 * PMS shell owns the sole landmark (SCRUM-352).
 */

import * as React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link, useNavigate } from 'react-router-dom';
import { z } from 'zod';
import { dollarsToCents } from '@artemis/validators';
import { Button, Card, Icon, TextField } from '@sparta/ui-kit';
import { usePropertyNameLookup } from '../../property/index.js';
import { useReservationTypes } from '../api/use-reservation-types.js';
import { useReservationAvailability } from '../api/use-reservation-availability.js';
import { useIngestReservation, type IngestInput } from '../api/use-ingest-reservation.js';
import { FormSelect, type FormSelectOption } from '../_components/FormSelect.js';
import { statusLabelFor } from '../_components/ResStatusPill.js';

/* ── money field — string INPUT validation only (conversion is @artemis) ──── */

/**
 * The net-remittance money FIELD schema — a plain `string` validator, NOT a
 * cents transform. It enforces the string INPUT rules only (required,
 * non-negative, numeric, ≤2 decimal places); the dollars→cents conversion is
 * done at submit via `dollarsToCents(Number(...))` from `@artemis/validators`
 * (the canonical Decimal / ROUND_HALF_UP path — CLAUDE.md rule 2). We do NOT
 * `.trim()`: a value carrying trailing whitespace (`'19.99 '`) is malformed
 * input and must be rejected verbatim, not silently repaired.
 *
 * Regex `^\d+(\.\d{1,2})?$`: at least one leading digit, an optional decimal
 * part of 1–2 digits. This rejects `''` (empty), `'-5'` / `'+5'` (signed),
 * `'.99'` (no leading digit), `'19.'` (trailing separator), `'1.234'` (>2 dp),
 * `'abc'` / `'NaN'` / `'Infinity'` / `'*'` (malformed), and every
 * injection-shaped string. The `.min(1)` message is what the required-field
 * UI surfaces for a blank submit.
 */
export const netRemittanceDollarsSchema: z.ZodType<string> = z
  .string()
  .min(1, 'Net remittance amount is required.')
  .regex(/^\d+(\.\d{1,2})?$/, 'Enter a valid amount — numbers only, up to 2 decimal places.');

/**
 * OPTIONAL money field (SCRUM-382 Channel Reference Notes). Same numeric rules as
 * the net-remittance field, but a BLANK value is allowed (the whole notes section
 * is optional). The regex `^(\d+(\.\d{1,2})?)?$` accepts the empty string OR a
 * non-negative amount with up to 2 decimals; it still rejects `'-5'`, `'.99'`,
 * `'5.'`, `'1.234'`, and injection-shaped strings. Dollars→cents conversion is
 * done at submit via `dollarsToCents` (the single canonical path).
 *
 * The integer part is capped at 12 digits (< $1 trillion — far above any real
 * per-booking channel figure) so an absurd value can never overflow
 * `dollarsToCents` into a thrown `cents()` range error. Without the cap the throw
 * would escape the submit handler (the conversion runs before its try/catch),
 * silently no-op'ing the form; the cap turns that into an honest field error.
 */
export const optionalMoneyDollarsSchema: z.ZodType<string> = z
  .string()
  .regex(/^(\d{1,12}(\.\d{1,2})?)?$/, 'Enter a valid amount — numbers only, up to 2 decimal places.');

/** Max length of the free-text channel-reference note (mirrors the wire schema). */
const CHANNEL_NOTE_MAX = 2_000;

/* ── local form schema — layers form-only fields onto the wire schema ─────── */

/**
 * The form schema LAYERS onto — never redefines — the wire
 * `ingestReservationSchema` (@artemis/validators; FE rule §4): the mutation
 * body is typed `IngestInput` and validated against the wire schema
 * server-side, so this local schema owns only the form-facing INPUT concerns
 * (a friendlier "Select a channel" message, the LIVE-status subset the picker
 * offers, the money-string field, and the cross-field date rule). `status` is
 * narrowed to the three LIVE blocking statuses the form offers (a subset of the
 * wire ingest enum). Dates are plain strings (an HTML `type="date"` emits a
 * bare `YYYY-MM-DD` that maps STRAIGHT to the branded `LocalDate` at the wire
 * boundary — the brand is applied server-side). Guest email + phone are
 * REQUIRED here (product) even though the wire allows null.
 */
const LIVE_STATUS_VALUES = ['BKD', 'CI', 'CO'] as const;
type LiveStatus = (typeof LIVE_STATUS_VALUES)[number];

const formSchema = z
  .object({
    propertyId: z.string().uuid({ message: 'Select a property.' }),
    reservationTypeKey: z.string().min(1, 'Select a channel.'),
    status: z.enum(LIVE_STATUS_VALUES),
    arrivalDate: z.string().min(1, 'Arrival is required.'),
    departureDate: z.string().min(1, 'Departure is required.'),
    channelReservationId: z.string(),
    // OPTIONAL occupancy. `valueAsNumber` on an EMPTY number input yields `NaN`;
    // the preprocess maps `NaN` → `undefined` so a blank field is a genuine
    // omission (never a 0/1 coercion), matching the wire's `.optional()` and
    // letting the BE apply its `?? 1` default. A supplied value is validated as
    // an integer ≥ 1, mirroring `ingestReservationSchema.guestCount`.
    guestCount: z.preprocess(
      (v) => (typeof v === 'number' && Number.isNaN(v) ? undefined : v),
      z
        .number({ invalid_type_error: 'Enter a whole number of guests.' })
        .int('Enter a whole number of guests.')
        .min(1, 'At least one guest is required.')
        .optional(),
    ),
    netRemittanceDollars: netRemittanceDollarsSchema,
    // SCRUM-382 — optional Channel Reference Notes (informational). All blank ⇒
    // the whole section is omitted from the wire body.
    grossGuestPaymentDollars: optionalMoneyDollarsSchema,
    otaCommissionDollars: optionalMoneyDollarsSchema,
    channelRemittedTaxDollars: optionalMoneyDollarsSchema,
    channelNote: z.string().max(CHANNEL_NOTE_MAX, 'Note is too long.'),
    firstName: z.string().trim().min(1, 'First name is required.'),
    lastName: z.string().trim().min(1, 'Last name is required.'),
    email: z.string().trim().min(1, 'Email is required.'),
    phone: z.string().trim().min(1, 'Phone is required.'),
  })
  .superRefine((values, ctx) => {
    // ISO YYYY-MM-DD is zero-padded → lexicographic compare == date compare.
    if (
      values.arrivalDate.length > 0 &&
      values.departureDate.length > 0 &&
      values.departureDate <= values.arrivalDate
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['departureDate'],
        message: 'Departure must be after arrival.',
      });
    }
  });

type FormValues = z.infer<typeof formSchema>;

const DEFAULT_VALUES: FormValues = {
  propertyId: '',
  reservationTypeKey: '',
  status: 'BKD',
  arrivalDate: '',
  departureDate: '',
  channelReservationId: '',
  guestCount: undefined,
  netRemittanceDollars: '',
  grossGuestPaymentDollars: '',
  otaCommissionDollars: '',
  channelRemittedTaxDollars: '',
  channelNote: '',
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
};

const STATUS_OPTIONS: ReadonlyArray<FormSelectOption<LiveStatus>> = LIVE_STATUS_VALUES.map(
  (value) => ({ id: value, label: statusLabelFor(value) }),
);

/* ── error copy (mapped from the ingest hook's tagged kind) ───────────────── */

function submitErrorMessageFor(kind: string, code?: string): string {
  switch (kind) {
    case 'conflict':
      return 'This channel booking conflicts with an existing reservation. Review the dates and try again.';
    case 'illegal':
      return illegalMessageFor(code);
    case 'forbidden':
      return 'You don’t have permission to record this channel reservation.';
    default:
      return 'Something went wrong recording the reservation. Try again.';
  }
}

function illegalMessageFor(code?: string): string {
  switch (code) {
    case 'unknown_reservation_type':
      return 'That channel can’t be recorded from here.';
    default:
      return 'This reservation can’t be recorded as entered. Check the channel, dates, and amount.';
  }
}

/* ── channel reference notes assembly (SCRUM-382) ─────────────────────────── */

/** A blank dollar string → undefined; otherwise cents via the canonical path. */
function dollarsToCentsOrUndefined(dollars: string): number | undefined {
  const trimmed = dollars.trim();
  if (trimmed === '') return undefined;
  return dollarsToCents(Number(trimmed));
}

/**
 * Build the OPTIONAL operator-supplied Channel Reference Notes from the form
 * values. Each figure omits when blank (never coerced to 0); the note omits when
 * blank/whitespace. Returns `undefined` when the whole section is empty
 * (omitted-stable) so the wire body never carries a structurally-empty object.
 * Provenance is NOT set here — the server stamps `'operator-supplied'`.
 */
function buildChannelReferenceNotes(
  values: FormValues,
): NonNullable<IngestInput['channelReferenceNotes']> | undefined {
  const grossGuestPaymentCents = dollarsToCentsOrUndefined(values.grossGuestPaymentDollars);
  const otaCommissionCents = dollarsToCentsOrUndefined(values.otaCommissionDollars);
  const channelRemittedTaxCents = dollarsToCentsOrUndefined(values.channelRemittedTaxDollars);
  const trimmedNote = values.channelNote.trim();
  const note = trimmedNote === '' ? undefined : trimmedNote;

  if (
    grossGuestPaymentCents === undefined &&
    otaCommissionCents === undefined &&
    channelRemittedTaxCents === undefined &&
    note === undefined
  ) {
    return undefined;
  }

  return {
    ...(grossGuestPaymentCents !== undefined ? { grossGuestPaymentCents } : {}),
    ...(otaCommissionCents !== undefined ? { otaCommissionCents } : {}),
    ...(channelRemittedTaxCents !== undefined ? { channelRemittedTaxCents } : {}),
    ...(note !== undefined ? { note } : {}),
  };
}

/* ── component ────────────────────────────────────────────────────────────── */

export function RecordChannelReservationForm(): React.ReactElement {
  const navigate = useNavigate();
  const ingest = useIngestReservation();
  const typesState = useReservationTypes();
  const nameLookup = usePropertyNameLookup();

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<FormValues>({
    mode: 'onSubmit',
    defaultValues: DEFAULT_VALUES,
    resolver: zodResolver(formSchema),
  });

  // `formAlert` covers the overbooked-quarantine, generic-non-success, and
  // caught-exception paths — all honest, non-celebratory, no internal ids.
  const [formAlert, setFormAlert] = React.useState<string | null>(null);

  // ── channel picker: ChannelCollect channel types only (SCRUM-350 tier-1) ──
  // Gated on TWO axes: (1) `collectionModel === 'ChannelCollect'` — the OTA is
  // the merchant of record, so the booking is net-remittance and is recorded
  // VERBATIM, never re-priced; and (2) a non-null `channelProcessor` — the
  // dedup-processor the ingest call keys on. PropertyCollect channel types
  // (VRBO / Booking / Expedia / Website) are EXCLUDED here even when they carry
  // a processor: the PMC is the merchant of record, so they go through the
  // normal priced local-reservation flow, not this net-remittance surface.
  const catalog = typesState.status === 'ready' ? typesState.data : [];
  const channelTypes = React.useMemo(
    () =>
      catalog.filter(
        (t) =>
          t.collectionModel === 'ChannelCollect' &&
          t.channelProcessor !== null &&
          t.channelProcessor !== undefined,
      ),
    [catalog],
  );
  const channelOptions: ReadonlyArray<FormSelectOption<string>> = channelTypes.map((t) => ({
    id: t.internalKey,
    label: t.displayName,
  }));

  // ── property picker options (id → name) ───────────────────────────────────
  const propertyOptions: ReadonlyArray<FormSelectOption<string>> =
    nameLookup.status === 'ready'
      ? Array.from(nameLookup.nameById.entries()).map(([id, name]) => ({ id, label: name }))
      : [];

  const propertyId = watch('propertyId');
  const reservationTypeKey = watch('reservationTypeKey');
  const status = watch('status');
  const arrivalDate = watch('arrivalDate');
  const departureDate = watch('departureDate');

  // ── overlap hard-block (conflict-group-aware, server truth) ───────────────
  const availability = useReservationAvailability({
    propertyId: propertyId.length > 0 ? propertyId : undefined,
    from: arrivalDate.length > 0 ? arrivalDate : undefined,
    to: departureDate.length > 0 ? departureDate : undefined,
  });

  // The exact condition under which `useReservationAvailability` is enabled:
  // all three window inputs present. This is what makes the overlap check a
  // TRUE hard block — whenever an availability check is OWED, submission cannot
  // proceed until it resolves `ready`. A `loading` window keeps the button
  // disabled; an `error` / `forbidden` window FAILS CLOSED in `onSubmit`
  // (never silently opens the block). The mutation fires ONLY on `ready` with
  // no blocking night.
  const needsAvailabilityCheck =
    propertyId.length > 0 && arrivalDate.length > 0 && departureDate.length > 0;
  const availabilityLoading = needsAvailabilityCheck && availability.status === 'loading';

  // The FIRST blocked night inside the half-open window `[arrival, departure)`.
  // No extra same-property filtering — the endpoint already returns the
  // EFFECTIVE (ADR-0026 conflict-group) set; we trust every entry verbatim.
  const blockingNight = React.useMemo(() => {
    if (availability.status !== 'ready') return null;
    if (arrivalDate.length === 0 || departureDate.length === 0) return null;
    return (
      availability.data.blockedNights.find(
        (night) => night.date >= arrivalDate && night.date < departureDate,
      ) ?? null
    );
  }, [availability, arrivalDate, departureDate]);

  // ── submit ────────────────────────────────────────────────────────────────
  const onSubmit = handleSubmit(async (values) => {
    setFormAlert(null);

    // FAIL CLOSED — the overlap check is a HARD gate. Whenever an availability
    // check is owed, the mutation is forbidden unless that check has resolved
    // `ready`. A `loading` window is mostly covered by the disabled submit
    // button, but is guarded here too as the true gate; an `error` /
    // `forbidden` window must NOT silently open the block (the pre-SCRUM-350
    // fail-open bug: `blockingNight` is null for every non-`ready` status).
    if (needsAvailabilityCheck && availability.status !== 'ready') {
      setFormAlert(
        availability.status === 'loading'
          ? 'Still verifying availability — try again in a moment.'
          : 'Couldn’t verify availability for these dates. Resolve the conflict check before recording.',
      );
      return;
    }

    // Pre-submit overlap block — never call the mutation on a known conflict.
    if (blockingNight !== null) return;

    const selected = channelTypes.find((t) => t.internalKey === values.reservationTypeKey);
    const processor = selected?.channelProcessor ?? null;
    // Defensive: the picker only offers non-null-processor types, so this is
    // unreachable in practice — surface an honest error rather than send a bad
    // wire body if the catalog ever drifts.
    if (selected === undefined || processor === null) {
      setFormAlert('That channel can’t be recorded from here. Pick a different channel.');
      return;
    }

    const trimmedKey = values.channelReservationId.trim();

    // SCRUM-382 — assemble the OPTIONAL operator-supplied Channel Reference Notes.
    // Each dollar field converts to cents via the single `dollarsToCents` path
    // (blank → omitted, never 0). The whole object is omitted when every field is
    // blank (omitted-stable) — the BE never sees a structurally-empty notes payload.
    const channelReferenceNotes = buildChannelReferenceNotes(values);

    const input: IngestInput = {
      propertyId: values.propertyId,
      reservationTypeKey: values.reservationTypeKey,
      processor,
      channelReservationId: trimmedKey === '' ? null : trimmedKey,
      arrivalDate: values.arrivalDate,
      departureDate: values.departureDate,
      status: values.status,
      netRemittanceCents: dollarsToCents(Number(values.netRemittanceDollars)),
      // OMIT `guestCount` when blank — never send 0/1; the BE defaults to 1.
      ...(values.guestCount !== undefined ? { guestCount: values.guestCount } : {}),
      guest: {
        displayName: `${values.firstName} ${values.lastName}`.trim(),
        email: values.email,
        phone: values.phone,
      },
      ...(channelReferenceNotes !== undefined ? { channelReferenceNotes } : {}),
    };

    try {
      const result = await ingest.mutateAsync(input);
      switch (result.outcome) {
        case 'created':
          navigate(`/reservations/${result.reservationId}`, {
            state: { flash: { kind: 'success', message: 'Channel reservation recorded.' } },
          });
          return;
        case 'updated':
          navigate(`/reservations/${result.reservationId}`, {
            state: {
              flash: {
                kind: 'info',
                message: 'This channel booking already existed — its details were updated.',
              },
            },
          });
          return;
        case 'noop':
          navigate(`/reservations/${result.reservationId}`, {
            state: {
              flash: {
                kind: 'info',
                message: 'This channel booking was already on file — nothing changed.',
              },
            },
          });
          return;
        case 'overbooked':
          // NOT a success. No navigate; no raw reservationId / processor slug.
          setFormAlert(
            'This booking overlaps an existing reservation and has been placed in quarantine as an overbooking. Resolve the conflict before it can take effect.',
          );
          return;
        default:
          // `undefined` / `stale` / any unknown outcome — generic non-success.
          setFormAlert(
            'The booking couldn’t be confirmed as recorded. Review it in the reservations list before relying on it.',
          );
          return;
      }
    } catch (err) {
      const kind =
        typeof err === 'object' && err !== null && 'kind' in err
          ? String((err as { kind: unknown }).kind)
          : 'error';
      const code =
        typeof err === 'object' &&
        err !== null &&
        'code' in err &&
        typeof (err as { code: unknown }).code === 'string'
          ? (err as { code: string }).code
          : undefined;
      setFormAlert(submitErrorMessageFor(kind, code));
    }
  });

  const isPending = ingest.status === 'pending';

  return (
    <div
      data-testid="record-channel-reservation-form"
      className="mx-auto flex w-full max-w-3xl flex-col gap-6 px-4 pt-5 pb-20 sm:px-10 sm:pt-7"
    >
      {/* Page chrome: back link + title + sub. */}
      <div>
        <Button variant="quiet" size="sm" className="-ml-2" onClick={() => navigate('/reservations')}>
          <Icon name="chevLeft" size={16} aria-hidden />
          <span className="ml-1">Reservations</span>
        </Button>
        <h1 className="m-0 mt-2 text-2xl font-semibold tracking-[-0.025em] text-[color:var(--fg-primary)]">
          Record a channel reservation
        </h1>
        <p className="m-0 mt-1.5 text-sm text-[color:var(--fg-secondary)]">
          Manually enter a live booking from a channel that collects payment on your behalf (e.g.
          Airbnb) that did not sync in.
        </p>
      </div>

      {/* Persistent honest "will NOT sync" banner. */}
      <div
        data-testid="channel-not-synced-banner"
        className="rounded-xl border border-solid border-[var(--border-default)] bg-[var(--bg-sunken)] p-3 text-sm text-[color:var(--fg-secondary)]"
      >
        <span className="font-semibold text-[color:var(--fg-primary)]">Heads up:</span> this
        records a channel booking that will not sync — it is entered manually and is not delivered
        by the channel. The net remittance is recorded exactly as entered and is not re-priced.
      </div>

      <form
        noValidate
        aria-label="Record a channel reservation"
        onSubmit={(e) => {
          e.preventDefault();
          void onSubmit();
        }}
        className="flex min-w-0 flex-1 flex-col gap-6"
      >
        {formAlert !== null ? (
          <div
            role="alert"
            aria-live="assertive"
            className="flex flex-col gap-2 rounded-xl border border-solid border-[var(--fg-danger)] bg-[var(--bg-surface)] p-3 text-sm text-[color:var(--fg-secondary)]"
          >
            <p className="m-0">{formAlert}</p>
            {availability.status === 'error' || availability.status === 'forbidden' ? (
              <div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => availability.refetch()}
                >
                  Retry availability check
                </Button>
              </div>
            ) : null}
          </div>
        ) : null}

        {blockingNight !== null ? (
          <div
            data-testid="overlap-block-alert"
            role="alert"
            aria-live="assertive"
            className="flex flex-col gap-2 rounded-xl border border-solid border-[var(--fg-danger)] bg-[var(--bg-surface)] p-3 text-sm text-[color:var(--fg-secondary)]"
          >
            <p className="m-0">
              These dates overlap an existing reservation for this property. Recording this booking
              would double-book the stay.
            </p>
            <Link
              to={`/reservations/${blockingNight.reservationId}`}
              className="font-semibold text-[color:var(--fg-primary)] underline"
            >
              View the conflicting reservation
            </Link>
          </div>
        ) : null}

        {/* ── Channel + stay ─────────────────────────────────────────────── */}
        <Card asChild>
          <section aria-labelledby="rc-channel-heading" className="flex flex-col gap-4">
            <h2
              id="rc-channel-heading"
              className="m-0 text-sm font-bold uppercase tracking-[0.08em] text-[color:var(--fg-tertiary)]"
            >
              Channel &amp; stay
            </h2>

            <FormSelect
              id="rc-channel"
              label="Channel"
              value={reservationTypeKey}
              onChange={(next) => setValue('reservationTypeKey', next)}
              options={channelOptions}
              placeholder="Select a channel"
              required
              disabled={typesState.status !== 'ready'}
              error={errors.reservationTypeKey?.message}
            />

            <FormSelect
              id="rc-property"
              label="Property"
              value={propertyId}
              onChange={(next) => setValue('propertyId', next)}
              options={propertyOptions}
              placeholder="Select a property"
              required
              disabled={nameLookup.status !== 'ready'}
              error={errors.propertyId?.message}
            />

            <FormSelect
              id="rc-status"
              label="Status"
              value={status}
              onChange={(next) => setValue('status', next as LiveStatus)}
              options={STATUS_OPTIONS}
              required
              error={errors.status?.message}
            />

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <TextField
                type="date"
                label="Arrival"
                error={errors.arrivalDate?.message}
                {...register('arrivalDate')}
              />
              <TextField
                type="date"
                label="Departure"
                error={errors.departureDate?.message}
                {...register('departureDate')}
              />
            </div>
          </section>
        </Card>

        {/* ── Remittance + channel id ────────────────────────────────────── */}
        <Card asChild>
          <section aria-labelledby="rc-money-heading" className="flex flex-col gap-4">
            <h2
              id="rc-money-heading"
              className="m-0 text-sm font-bold uppercase tracking-[0.08em] text-[color:var(--fg-tertiary)]"
            >
              Remittance
            </h2>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <TextField
                inputMode="decimal"
                label="Net remittance (USD)"
                hint="The amount the channel remits, entered verbatim — not re-priced."
                error={errors.netRemittanceDollars?.message}
                {...register('netRemittanceDollars')}
              />
              <TextField
                label="Channel reservation ID"
                hint="Fill if known — enables future reconciliation. Leave blank if unknown; it will never sync."
                error={errors.channelReservationId?.message}
                {...register('channelReservationId')}
              />
            </div>
          </section>
        </Card>

        {/* ── Channel reference notes (informational — SCRUM-382) ─────────── */}
        <Card asChild>
          <section aria-labelledby="rc-notes-heading" className="flex flex-col gap-4">
            <div>
              <h2
                id="rc-notes-heading"
                className="m-0 text-sm font-bold uppercase tracking-[0.08em] text-[color:var(--fg-tertiary)]"
              >
                Channel reference notes
              </h2>
              <p className="m-0 mt-1.5 text-sm text-[color:var(--fg-secondary)]">
                Optional. The channel-reported gross, commission, and taxes, entered manually for
                your reference only. These figures are informational — they are not part of the
                ledger, are not re-priced, and the guest balance stays $0.
              </p>
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <TextField
                inputMode="decimal"
                label="Gross guest payment (USD)"
                error={errors.grossGuestPaymentDollars?.message}
                {...register('grossGuestPaymentDollars')}
              />
              <TextField
                inputMode="decimal"
                label="OTA commission (USD)"
                error={errors.otaCommissionDollars?.message}
                {...register('otaCommissionDollars')}
              />
              <TextField
                inputMode="decimal"
                label="Channel-remitted taxes (USD)"
                error={errors.channelRemittedTaxDollars?.message}
                {...register('channelRemittedTaxDollars')}
              />
            </div>
            <TextField
              label="Note"
              hint="Any free-text reference note (e.g. reconciled against the channel payout)."
              error={errors.channelNote?.message}
              {...register('channelNote')}
            />
          </section>
        </Card>

        {/* ── Guest ──────────────────────────────────────────────────────── */}
        <Card asChild>
          <section aria-labelledby="rc-guest-heading" className="flex flex-col gap-4">
            <h2
              id="rc-guest-heading"
              className="m-0 text-sm font-bold uppercase tracking-[0.08em] text-[color:var(--fg-tertiary)]"
            >
              Guest
            </h2>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <TextField
                label="Guest first name"
                error={errors.firstName?.message}
                {...register('firstName')}
              />
              <TextField
                label="Guest last name"
                error={errors.lastName?.message}
                {...register('lastName')}
              />
              <TextField
                type="email"
                label="Email"
                error={errors.email?.message}
                {...register('email')}
              />
              <TextField
                type="tel"
                label="Phone"
                error={errors.phone?.message}
                {...register('phone')}
              />
              <TextField
                type="number"
                inputMode="numeric"
                min={1}
                step={1}
                label="Number of guests"
                hint="Optional — leave blank to default to 1."
                error={errors.guestCount?.message}
                {...register('guestCount', { valueAsNumber: true })}
              />
            </div>
          </section>
        </Card>

        {/* ── Actions ────────────────────────────────────────────────────── */}
        <div className="flex flex-wrap items-center justify-end gap-2 border-t border-[var(--border-subtle)] pt-4">
          {availabilityLoading ? (
            <p
              id="rc-availability-status"
              role="status"
              aria-live="polite"
              className="m-0 mr-auto text-sm text-[color:var(--fg-secondary)]"
            >
              Checking availability…
            </p>
          ) : null}
          <Button type="button" variant="ghost" onClick={() => navigate('/reservations')}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={isPending || availabilityLoading}
            aria-describedby={availabilityLoading ? 'rc-availability-status' : undefined}
          >
            Record channel reservation
          </Button>
        </div>
      </form>
    </div>
  );
}
